/**
 * A hall's files, kept on the device by their content hash. Spark asks for the scan's header and
 * chunks through this; each comes from the device's cache when it has it, otherwise from the file
 * store through a short-lived signed link, is checked against its SHA-256 and then kept. A file is
 * downloaded again only when its hash changes. Dropped connections are retried with growing waits;
 * an expired link is renewed once and retried.
 */

import { fileUrl, type FileAccess, type FileRef, type SplatSet } from '@mawkeb/shared/manifest';

export const CACHE_NAME = 'venue-files-v1';

export interface FileEvents {
  /** A file arrived: how big, and whether it came from the device. */
  onFile?: (file: FileRef, fromDevice: boolean) => void;
  /** Downloads keep failing; the app tells the person and keeps trying. */
  onTrouble?: (message: string | null) => void;
}

export interface Deps {
  fetch: typeof fetch;
  caches: CacheStorage | null;
  digest: (data: ArrayBuffer) => Promise<string>;
  sleep: (ms: number) => Promise<void>;
}

const defaultDeps = (): Deps => ({
  fetch: (...a) => fetch(...a),
  caches: typeof caches !== 'undefined' ? caches : null,
  digest: async (data) => {
    const h = await crypto.subtle.digest('SHA-256', data);
    return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
  },
  sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
});

class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

function abortError(): Error {
  return new DOMException('The download was cancelled', 'AbortError');
}

export class HallFiles {
  private readonly byName = new Map<string, FileRef>();
  private readonly inFlight = new Map<string, Promise<Uint8Array>>();
  private root: Promise<Uint8Array> | null = null;
  private renewing: Promise<FileAccess> | null = null;
  private readonly deps: Deps;
  readonly seen = new Set<string>();

  constructor(
    readonly set: SplatSet,
    private access: FileAccess,
    private readonly renewAccess: () => Promise<FileAccess>,
    private readonly events: FileEvents = {},
    deps?: Partial<Deps>,
  ) {
    this.deps = { ...defaultDeps(), ...deps };
    for (const f of [set.root, ...set.chunks]) this.byName.set(f.name, f);
  }

  // ---- Spark's PagedSplatsFetcher

  async fetchRange(signal: AbortSignal, offset: number, bytes: number): Promise<Uint8Array> {
    this.root ??= this.get(this.set.root, signal).catch((e) => {
      this.root = null;
      throw e;
    });
    const data = await this.root;
    const end = Math.min(data.length, offset + bytes);
    return data.slice(Math.min(offset, end), end);
  }

  async fetchChunkFile(signal: AbortSignal, name: string): Promise<Uint8Array> {
    const base = name.split('/').pop() ?? name;
    const file = this.byName.get(base);
    if (!file) throw new Error(`the hall's manifest doesn't list ${base}`);
    const data = await this.get(file, signal);
    return data.slice();
  }

  async fetchChunkById(): Promise<Uint8Array> {
    throw new Error('chunked RAD files are fetched by name');
  }

  // ---- the device cache and the network

  /** One file, from the device or the network; concurrent asks for the same file share one download. */
  get(file: FileRef, signal?: AbortSignal): Promise<Uint8Array> {
    let p = this.inFlight.get(file.sha256);
    if (!p) {
      p = this.load(file, signal).finally(() => this.inFlight.delete(file.sha256));
      this.inFlight.set(file.sha256, p);
    }
    return p;
  }

  private cacheKey(file: FileRef): string {
    return `${location.origin}/__venue-file/${file.sha256}`;
  }

  private async load(file: FileRef, signal?: AbortSignal): Promise<Uint8Array> {
    const cached = await this.fromDevice(file);
    if (cached) {
      this.note(file, true);
      return cached;
    }
    const data = await this.download(file, signal);
    await this.keep(file, data);
    this.note(file, false);
    return data;
  }

  private note(file: FileRef, fromDevice: boolean) {
    if (this.seen.has(file.sha256)) return;
    this.seen.add(file.sha256);
    this.events.onFile?.(file, fromDevice);
  }

  private async fromDevice(file: FileRef): Promise<Uint8Array | null> {
    const c = this.deps.caches;
    if (!c) return null;
    try {
      const cache = await c.open(CACHE_NAME);
      const hit = await cache.match(this.cacheKey(file));
      if (!hit) return null;
      const buf = await hit.arrayBuffer();
      if (buf.byteLength !== file.bytes) {
        await cache.delete(this.cacheKey(file));
        return null;
      }
      return new Uint8Array(buf);
    } catch {
      return null;
    }
  }

  private async keep(file: FileRef, data: Uint8Array) {
    const c = this.deps.caches;
    if (!c) return;
    try {
      const cache = await c.open(CACHE_NAME);
      await cache.put(
        this.cacheKey(file),
        new Response(data.slice().buffer as ArrayBuffer, { headers: { 'content-type': 'application/octet-stream', 'content-length': String(data.byteLength) } }),
      );
    } catch {
      // The device is full or storage is blocked: the hall still works, it just downloads again next time.
    }
  }

  private async download(file: FileRef, signal?: AbortSignal): Promise<Uint8Array> {
    const waits = [500, 1000, 2000, 4000, 8000, 15000];
    let renewed = false;
    for (let attempt = 0; ; attempt++) {
      if (signal?.aborted) throw abortError();
      try {
        const res = await this.deps.fetch(fileUrl(this.access, file.key), { signal, cache: 'no-store' });
        if (res.status === 401 || res.status === 403) {
          if (renewed) throw new HttpError(res.status, 'the file store refused the link');
          renewed = true;
          this.access = await this.renew();
          attempt--;
          continue;
        }
        if (!res.ok) throw new HttpError(res.status, `the file store answered ${res.status}`);
        const buf = await res.arrayBuffer();
        if (buf.byteLength !== file.bytes) throw new Error(`${file.name} arrived cut short`);
        const sha = await this.deps.digest(buf);
        if (sha !== file.sha256) throw new Error(`${file.name} arrived damaged`);
        this.events.onTrouble?.(null);
        return new Uint8Array(buf);
      } catch (e) {
        if ((e as Error).name === 'AbortError') throw e;
        if (e instanceof HttpError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) throw e;
        if (attempt >= 1) this.events.onTrouble?.('The connection dropped. Still loading the hall, retrying.');
        const wait = waits[Math.min(attempt, waits.length - 1)]!;
        await this.deps.sleep(wait);
      }
    }
  }

  private renew(): Promise<FileAccess> {
    this.renewing ??= this.renewAccess().finally(() => {
      this.renewing = null;
    });
    return this.renewing;
  }

  /**
   * After the first view is in, quietly fetches every file not yet on the device, one at a time,
   * so the whole hall works offline next time. `paused` lets the app hold it while someone moves.
   */
  async keepAll(paused: () => boolean, signal: AbortSignal): Promise<void> {
    for (const f of [this.set.root, ...this.set.chunks]) {
      while (paused() && !signal.aborted) await this.deps.sleep(400);
      if (signal.aborted) return;
      if (this.seen.has(f.sha256)) continue;
      try {
        await this.get(f, signal);
      } catch {
        return;
      }
    }
  }
}

/**
 * Whether to fetch the rest of the hall in the background after the first view: yes on a laptop or
 * in the installed app, never with Data Saver on or on mobile data (where the browser can tell).
 */
export function mayKeepAll(laptop: boolean): boolean {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean; type?: string } };
  if (nav.connection?.saveData) return false;
  if (nav.connection?.type === 'cellular') return false;
  const installed = matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return laptop || installed;
}

/** Whether a device should load the lighter copy of the scan. */
export function wantsLightScan(tierName: string): boolean {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  return tierName === 'phone-light' || tierName === 'phone-older' || nav.connection?.saveData === true;
}

/** Asks the browser not to clear the hall's files when space runs low (installed apps usually get this). */
export async function askToKeepFiles(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
